import { Formik } from 'formik';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { DualListBox, FormModal, GFormFields } from '@components';
import { getWAFWhiteListFormConfig, getWhiteListDualListBoxConfig, wafWhiteListFormSchema } from '@constants';
import { ApiEndpoint, ApiType, FormActionType, FormType } from '@enums';
import { useAppDispatch, useFetchFormList, useFormContent, useFormList, useFormProcedure } from '@hooks';
import { submitFormRequest, useCdnSummary } from '@store';
import i18n from '@translations';
import type { DialogBase, FormConfig, WhitelistWAFForm } from '@types';
import { convertOptionList, validateValueEqual } from '@utils';

export const WAFWhiteListModal: FC<DialogBase> = ({ open, onClose }) => {
  const { id: application_id } = useParams();
  const dispatch = useAppDispatch();

  const formType = FormType.CdnWAF;
  const endpoint = ApiEndpoint.WHITELIST;

  const currentProcedure = useFormProcedure(formType);
  const currentForm = useFormContent({ formType });
  const isEdit = validateValueEqual(currentProcedure, FormActionType.Edit);
  const CdnConfigurationSummary = useCdnSummary();

  const { options: domainOptions } = useMemo(
    () =>
      convertOptionList({
        originalOptions: CdnConfigurationSummary?.domain,
        output: { value: 'domain', label: 'domain' },
      }),
    [CdnConfigurationSummary?.domain],
  );

  const handleSubmit = (values: WhitelistWAFForm) => {
    dispatch(
      submitFormRequest({
        formType,
        params: isEdit ? { content_id: currentForm?.id } : { application_id },
        formData: { ...values },
        globalOptions: {
          refetch_id: application_id,
          refetch_endpoint: ApiEndpoint.WHITELIST,
          endpoint: ApiEndpoint.WHITELIST,
          returnResult: true,
          apiType: isEdit ? ApiType.PUT : ApiType.POST,
        },
      }),
    );
  };

  useFetchFormList({
    formType: FormType.CdnWAF,
    params: { application_id, page_size: 100 },
    globalOptions: {
      endpoint: ApiEndpoint.RULE,
    },
  });

  const ruleList = useFormList({ formType, endpoint: ApiEndpoint.RULE });

  const ruleOptions = ruleList.map(({ id, rule_name }) => ({
    value: id,
    label: rule_name,
  }));

  const schema = useMemo(
    () => wafWhiteListFormSchema({ optionList: domainOptions, skipRuleOption: ruleOptions }),
    [domainOptions, ruleOptions],
  );

  const dualListBoxConfig = useMemo(
    () => getWhiteListDualListBoxConfig({ options: ruleOptions }),
    [ruleOptions, i18n.language],
  );

  const formConfig: FormConfig = useMemo(
    () =>
      getWAFWhiteListFormConfig({
        endpoint,
        isEdit,
        schema,
      }),
    [endpoint, isEdit, i18n.language],
  );

  const initialValues: WhitelistWAFForm = useMemo(
    () => ({
      name: currentForm?.name ?? '',
      rulesets: currentForm?.rulesets?.map((ruleset) => ruleset.cdn_global_waf_rule_id) ?? [],
      conditions: currentForm?.conditions ?? [],
    }),
    [currentForm],
  );

  const { inputList, validationSchema, label } = formConfig;

  if (!open || !inputList) return;

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      <FormModal
        title={label?.title}
        description={label?.description}
        onClose={onClose}
        formType={formType}
        open={open}
        size="xl"
        confirmButton={{ children: label?.confirmButton }}
      >
        <GFormFields inputList={inputList} />
        <DualListBox {...dualListBoxConfig} />
      </FormModal>
    </Formik>
  );
};
